import type { LatLngLiteral } from '@shared/core/google-maps/types';
import type { HotelBoundsKey } from './Map.types';

type HotelLike = Readonly<{ location?: Readonly<{ lat?: number | null; lng?: number | null }> | null }>;

// Type guard: keeps a legitimate 0 (equator / prime meridian) while rejecting
// null/undefined/NaN, which would otherwise poison the bounding box.
const isFiniteCoordinate = (value: number | null | undefined): value is number => Number.isFinite(value);

/**
 * Collect valid lat/lng markers from hotels, skipping any entry whose coordinates are
 * missing or non-finite.
 */
export const collectHotelMarkers = (hotels: ReadonlyArray<HotelLike> | undefined): LatLngLiteral[] => {
  const markers: LatLngLiteral[] = [];
  for (const hotel of hotels ?? []) {
    const { lat, lng } = hotel.location ?? {};
    if (!isFiniteCoordinate(lat) || !isFiniteCoordinate(lng)) continue;
    markers.push({ lat, lng });
  }
  return markers;
};

/**
 * Quantize the marker bounding box to a stable key (1e-5 deg ≈ 1m) so negligible
 * viewport jitter doesn't repeatedly re-trigger auto-fit. Returns null when empty.
 */
export const computeHotelBoundsKey = (markers: ReadonlyArray<LatLngLiteral>): HotelBoundsKey | null => {
  if (!markers.length) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const { lat, lng } of markers) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }

  const quantizeToE5 = (value: number) => Math.round(value * 1e5);
  return {
    count: markers.length,
    minLatE5: quantizeToE5(minLat),
    maxLatE5: quantizeToE5(maxLat),
    minLngE5: quantizeToE5(minLng),
    maxLngE5: quantizeToE5(maxLng),
  };
};

/**
 * Structural equality for two bounds keys. A null on either side means there is no
 * comparable previous bounds, so the keys are treated as different.
 */
export const areHotelBoundsKeysEqual = (a: HotelBoundsKey | null, b: HotelBoundsKey | null): boolean => {
  if (!a || !b) return false;
  return a.count === b.count && a.minLatE5 === b.minLatE5 && a.maxLatE5 === b.maxLatE5 && a.minLngE5 === b.minLngE5 && a.maxLngE5 === b.maxLngE5;
};
